import React from 'react';
import type {
  MedicalDocument,
  MedicalTimeline,
  TimelineEvent,
} from '../../api/documents';
import MemberActivityFeed from './MemberActivityFeed';
import MemberCriticalFindings from './MemberCriticalFindings';
import MemberHealthSnapshot from './MemberHealthSnapshot';
import MemberActionsHub from './MemberActionsHub';
import type { MemberTabId } from './MemberTabs';

interface MemberOverviewTabProps {
  timeline: MedicalTimeline | null;
  documents: MedicalDocument[];
  timelineLoading: boolean;
  onSwitchTab: (tab: MemberTabId, opts?: { severity?: 'important' | 'critical' | 'all-marked' }) => void;
  onUpload: () => void;
  onGenerateTimeline: () => void;
}

const HighlightIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
    <path d="m12 3 1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3Z" />
  </svg>
);

const FeedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const FindingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SnapshotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 2v6L4.2 17.5A2 2 0 0 0 6 20h12a2 2 0 0 0 1.8-2.5L15 8V2" />
    <path d="M8 2h8" />
    <path d="M7 14h10" />
  </svg>
);

const ActionsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const MemberOverviewTab: React.FC<MemberOverviewTabProps> = ({
  timeline,
  documents,
  timelineLoading,
  onSwitchTab,
  onUpload,
  onGenerateTimeline,
}) => {
  const hasTimeline = Boolean(timeline);
  const highlights = timeline?.highlights ?? [];
  const importantCount =
    (timeline?.events ?? []).filter(
      (e) => e.severity === 'important' || e.severity === 'critical',
    ).length;

  const openFindingInTimeline = (_ev: TimelineEvent) => {
    onSwitchTab('timeline', { severity: 'all-marked' });
  };

  return (
    <div className="member-overview">
      {/* A. Concluzii cheie — full width */}
      <div className="ov-full-width">
        {hasTimeline && highlights.length > 0 ? (
          <div className="ov-highlights">
            <h3 className="ov-highlights-title">
              <HighlightIcon />
              Concluzii cheie
            </h3>
            <ul className="ov-highlights-list">
              {highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="ov-section">
            <div className="ov-empty" role="status">
              <HighlightIcon className="ov-empty-icon"/>
              <div className="ov-empty-title">Cronologia nu a fost încă generată</div>
              <div className="ov-empty-text">
                Generează o cronologie pentru a obține concluziile cheie din documente.
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={timelineLoading}
                onClick={() => {
                  onSwitchTab('timeline');
                  onGenerateTimeline();
                }}
              >
                {timelineLoading ? (
                  <>
                    <span className="spinner" style={{ width: 12, height: 12 }} />
                    Generare…
                  </>
                ) : (
                  'Generează cronologie'
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* B. Activitate recentă — left column on desktop */}
      <div className="ov-section">
        <div className="ov-section-header">
          <h3 className="ov-section-title">
            <FeedIcon />
            Activitate recentă
          </h3>
          <button
            type="button"
            className="ov-section-action"
            onClick={() => onSwitchTab('timeline')}
          >
            Vezi toată cronologia →
          </button>
        </div>
        <MemberActivityFeed
          timeline={timeline}
          documents={documents}
          onSwitchTab={onSwitchTab}
        />
      </div>

      {/* C. Constatări importante — right column on desktop */}
      <div className="ov-section">
        <div className="ov-section-header">
          <h3 className="ov-section-title">
            <FindingsIcon />
            Constatări importante
          </h3>
          {importantCount > 0 && (
            <button
              type="button"
              className="ov-section-action"
              onClick={() => onSwitchTab('timeline', { severity: 'all-marked' })}
            >
              Vezi toate →
            </button>
          )}
        </div>
        <MemberCriticalFindings
          timeline={timeline}
          onOpenInTimeline={openFindingInTimeline}
        />
      </div>

      {/* D. Snapshot sănătate — left column, below activity */}
      <div className="ov-section">
        <div className="ov-section-header">
          <h3 className="ov-section-title">
            <SnapshotIcon />
            Snapshot sănătate
          </h3>
        </div>
        <MemberHealthSnapshot timeline={timeline} onSwitchTab={onSwitchTab} />
      </div>

      {/* E. Hub acțiuni — right column, below findings */}
      <div className="ov-section">
        <div className="ov-section-header">
          <h3 className="ov-section-title">
            <ActionsIcon />
            Acțiuni rapide
          </h3>
        </div>
        <MemberActionsHub
          hasTimeline={hasTimeline}
          timelineLoading={timelineLoading}
          onUpload={onUpload}
          onGenerateTimeline={onGenerateTimeline}
          onSwitchTab={onSwitchTab}
        />
      </div>
    </div>
  );
};

export default MemberOverviewTab;
